<script setup>
import { visualMode, hasLottie } from '~/utils/block-visual.js'

// Components
import { defineAsyncComponent } from 'vue'
const GlobalButton = defineAsyncComponent(() =>
  import('~/components/globals/Button.vue')
)

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

// Computed
const positionClass = computed(() => {
  const position = props.block?.position

  if (position === 'imageLeft') {
    return '--image-left'
  }

  if (position === 'fullWidth') {
    return '--full-width'
  }

  // Default: imageRight
  return '--image-right'
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section
  :class="['section-mb text-and-image', positionClass]"
  :id="block?.['anchorId']"
>
  <div class="container text-and-image__container">
    <div class="text-and-image__content">
      <div class="text-and-image__heading-container">
        <div v-html="block.heading?.html"
            class="text-and-image__heading"></div>
        <p v-if="block.description?.length"
            class="text-and-image__description">
          {{ block.description }}
        </p>
      </div>

      <div v-if="block.imageTextMatrix?.length"
        class="text-and-image__list">

        <template v-for="(item, index) in block.imageTextMatrix" :key="item.id">
          <div v-if="item.typeHandle === 'iconTextLink'"
            class="text-and-image__icon-text-link" :key="item.id">

            <div class="text-and-image__icon-text-link-head">
              <span class="text-and-image__icon-text-link-head-icon">
                <img :src="(item.iconField || [])[0]?.url"
                  :alt="`List Icon ${index + 1}`"
                  :title="`List Icon ${index + 1}`"
                  loading="lazy"
                  decoding="async"
                  fetchpriority="low"
                  width="24"
                  height="24"
                >
              </span>
              <span class="text-and-image__icon-text-link-head-text">{{ item.title }}</span>
            </div>

            <p v-if="item.description?.length"
              class="text-and-image__icon-text-link-text">
              {{ item.description }}
            </p>

            <a v-if="item.linkTitle?.url"
              :href="item.linkTitle?.url"
              :title="item.linkTitle?.title"
              class="text-and-image__icon-text-link-button">
              {{ item.linkTitle?.title }}
            </a>
          </div>

          <div v-else
            class="text-and-image__list-item">

            <div class="flex">
              <div class="text-and-image__list-item-image">
                <img :src="(item.image || [])[0]?.url"
                  alt=""
                  loading="lazy"
                  aria-hidden="true"
                >
              </div>


              <p class="text-and-image__list-item-title">{{ item.title }}</p>
            </div>

            <p v-if="item.text?.length"
              class="text-and-image__list-item-text">
              {{ item.text }}
            </p>

          </div>
        </template>

      </div>

      <GlobalButton
        v-if="block.linkTitle?.url"
        class="text-and-image__button"
        :class="block.linkTitle?.class"
        :url="block.linkTitle?.url"
        :title="block.linkTitle?.title"
        :type="block?.buttonSettings?.linkType"
        :size="block?.buttonSettings?.linkSize"
        :icon="block?.buttonSettings?.linkIcon"
        :popup-enabled="Boolean(block?.buttonSettings?.popupEnabled)"
      />
    </div>

    <div class="text-and-image__asset">
      <GlobalsLottieVisual v-if="visualMode(block) === 'lottie' && hasLottie(block)" :entry="block" />
      <GlobalsConversationImage v-else-if="visualMode(block) === 'kv' && props.block?.conversationBox?.length" :entry="props.block" desktop-field="imageDesktop" mobile-field="imageMobile" />
      <GlobalsImage v-else
        :landscape="block.imageDesktop"
        :portrait="block.imageMobile"
        :alt="block.heading?.plainText.trim()"
        :max-width="block.position === 'fullWidth' ? 1552 : 886"
        :quality="100"
      />
    </div>
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.text-and-image__container {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.text-and-image__asset {
  border-radius: 6.356px;
  overflow: hidden;
}

.text-and-image__heading-container {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 1024px) {
    gap: 20px;
  }
}

.text-and-image__heading :deep(*) {
  font-size: 28px;
  line-height: 120%;
  letter-spacing: -0.56px;
  color: var(--color-gray-800);

  @media (min-width: 1024px) {
    font-size: 38px;
    line-height: 120%;
    letter-spacing: -0.76px;
  }
}

.text-and-image__heading :deep(p) {
  font-size: 16px;
  line-height: 136%;
  letter-spacing: -0.32px;
  color: var(--color-gray-500);
}

.text-and-image__heading :deep(p:has(strong)) {
  margin-bottom: 16px;

  @media (min-width: 1024px) {
    margin-bottom: 12px;
  }
}

.text-and-image__heading :deep(p strong) {
  font-size: 20px;
  font-weight: 500 !important;
  line-height: 128%;
  letter-spacing: -0.4px;
  color: var(--color-gray-700);

  @media (min-width: 1024px) {
    font-size: 22px;
    line-height: 144%;
    letter-spacing: -0.44px;
  }
}

.text-and-image__heading :deep(p + p:has(strong)) {
  margin-top: 32px;

  @media (min-width: 1024px) {
    margin-top: 24px;
  }
}

.text-and-image__description {
  line-height: 140%; /* 22.4px */
  letter-spacing: -0.32px;
  color: var(--color-gray-500);

  @media (min-width: 1024px) {
    font-size: 18px;
    line-height: 138%; /* 24.84px */
    letter-spacing: -0.36px;
    color: var(--color-gray-400);
  }
}

.text-and-image__list {
  margin-top: 36px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 1024px) {
    margin-top: 48px;
  }
}

.text-and-image__list-item-content {
  display: flex;
}

.text-and-image__list-item-image {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-right: 8px;

  @media (min-width: 1024px) {
    margin-top: 2px;
  }
}

.text-and-image__list-item-title {
  font-size: 18px;
  line-height: 138%;
  letter-spacing: -0.36px;
  color: var(--color-gray-700);

  @media (min-width: 1024px) {
    font-size: 20px;
    line-height: 136%; /* 27.2px */
    letter-spacing: -0.4px;
  }
}

.text-and-image__list-item-text {
  font-size: 16px;
  line-height: 140%; /* 22.4px */
  letter-spacing: -0.32px;
  color: var(--color-gray-400);
  margin-top: 4px;
}


@media (min-width: 1024px) {

  .text-and-image__container {
    flex-direction: row;
    justify-content: space-between;
  }

  .--image-left .text-and-image__container {
    flex-direction: row-reverse;
  }

  .text-and-image__asset {
    width: calc(866 / 1552 * 100%);
    flex-shrink: 0;
    border-radius: 16px;
    max-height: 660px;
  }

  .text-and-image__content {
    width: calc(572 / 1552 * 100%);
    flex-shrink: 0;
    margin-bottom: 0;
  }

  .--full-width .text-and-image__container {
    flex-direction: column;
  }

  .--full-width .text-and-image__content,
  .--full-width .text-and-image__asset {
    width: 100%;
  }

  .--full-width .text-and-image__asset {
    max-height: none;
  }

  .--full-width .text-and-image__asset :deep(img) {
    height: auto;
  }

}

.text-and-image__button {
  margin-top: 32px;

  @media (min-width: 1024px) {
    min-width: 160px;
    margin-top: 48px;
  }
}

.text-and-image__content:has(.text-and-image__icon-text-link) {
  @media (min-width: 1440px) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
  }
}

.text-and-image__list:has(.text-and-image__icon-text-link) {
  margin-top: 36px;

  @media (min-width: 1024px) {
    margin-top: 32px;
  }
}

.text-and-image__icon-text-link {
  display: flex;
  flex-direction: column;
}

.text-and-image__icon-text-link-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 500;
  line-height: 138%; /* 24.84px */
  letter-spacing: -0.36px;
  color: var(--color-gray-700);
  margin-bottom: 6px;

  @media (min-width: 1440px) {
    font-size: 20px;
    line-height: 120%; /* 24px */
    letter-spacing: -0.4px;
    margin-bottom: 12px;
  }
}

.text-and-image__icon-text-link-head-icon {
  width: 24px;
  height: 24px;

  & :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.text-and-image__icon-text-link-text {
  font-size: 14px;
  line-height: 136%; /* 19.04px */
  letter-spacing: -0.28px;
  color: var(--color-gray-400);

  @media (min-width: 1440px) {
    font-size: 15px;
    line-height: 146%; /* 21.9px */
    letter-spacing: -0.3px;
  }
}

.text-and-image__icon-text-link-button {
  margin-top: 20px;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: -0.32px;
  text-decoration: underline;
  color: #000;
}

.text-and-image__asset-mobile {
  margin-top: 36px;
  border-radius: 12px;
  overflow: hidden;

  @media (min-width: 1024px) {
    display: none;
  }
}

</style>
